import React from 'react';
import { Alert, Button, Modal, Tag } from 'antd-v5';
import DOMPurify from 'dompurify';

import style from './index.module.scss';

const REQUIRED_READING_SECONDS = 8;
const TIMER_INTERVAL_MS = 1000;
const CONTENT_FIT_TOLERANCE_PX = 2;
const SCROLL_BOTTOM_TOLERANCE_PX = 8;
const MODAL_WIDTH_PX = 860;

class LegalConfirmationModal extends React.Component {
  state = {
    secondsLeft: REQUIRED_READING_SECONDS,
    scrolledToBottom: false,
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.startReading();
    }
    if (!this.props.visible && prevProps.visible) {
      this.stopTimer();
    }
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  startReading = () => {
    this.stopTimer();
    this.setState({ secondsLeft: REQUIRED_READING_SECONDS, scrolledToBottom: false }, () => {
      window.setTimeout(this.checkContentAlreadyAtBottom, 0);
    });
    this.timer = window.setInterval(() => {
      this.setState(previousState => ({
        secondsLeft: Math.max(0, previousState.secondsLeft - 1),
      }), () => {
        if (this.state.secondsLeft === 0) {
          this.stopTimer();
        }
      });
    }, TIMER_INTERVAL_MS);
  };

  stopTimer = () => {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  checkContentAlreadyAtBottom = () => {
    const container = this.contentRef;
    if (container && container.scrollHeight <= container.clientHeight + CONTENT_FIT_TOLERANCE_PX) {
      this.setState({ scrolledToBottom: true });
    }
  };

  handleScroll = event => {
    const target = event.currentTarget;
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - SCROLL_BOTTOM_TOLERANCE_PX;
    if (reachedBottom && !this.state.scrolledToBottom) {
      this.setState({ scrolledToBottom: true });
    }
  };

  isConfirmationEnabled = () => this.state.secondsLeft === 0 && this.state.scrolledToBottom;

  formatAgreementName = name => {
    const normalizedName = String(name || '').trim();
    return normalizedName.startsWith('《') && normalizedName.endsWith('》')
      ? normalizedName
      : `《${normalizedName}》`;
  };

  renderAgreement = (agreement, index) => (
    <section className={style.agreement} key={agreement.id}>
      <div className={style.agreementHeading}>
        <span>{index + 1}. {this.formatAgreementName(agreement.name)}</span>
        {agreement.effective_at_text && <Tag color="blue">发布于 {agreement.effective_at_text}</Tag>}
      </div>
      <div
        className={style.agreementContent}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(agreement.content || '') }}
      />
    </section>
  );

  render() {
    const {
      visible,
      agreements = [],
      pageTitle = '供应商平台协议与规则确认',
      pageCopy = '',
      submitting,
      onCancel,
      onConfirm,
    } = this.props;
    const { secondsLeft, scrolledToBottom } = this.state;
    const readingStatus = scrolledToBottom ? '已阅读至规则最底部' : '请阅读至规则最底部';
    const readingDescription = `${pageCopy || '请完整阅读以下内容。'}还需等待${secondsLeft}秒，${readingStatus}。`;

    return (
      <Modal
        open={visible}
        title={pageTitle}
        width={MODAL_WIDTH_PX}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        destroyOnClose
        onCancel={onCancel}
        footer={[
          <Button key="cancel" disabled={submitting} onClick={onCancel}>取消并返回</Button>,
          <Button
            key="confirm"
            type="primary"
            loading={submitting}
            disabled={!this.isConfirmationEnabled()}
            onClick={() => onConfirm({ scrolledToBottom: true })}
          >
            我已阅读并同意以上全部规则
          </Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          message={`本次需要确认 ${agreements.length} 份协议与规则`}
          description={readingDescription}
          className={style.alert}
        />
        <div
          className={style.content}
          ref={ref => { this.contentRef = ref; }}
          onScroll={this.handleScroll}
        >
          {agreements.map(this.renderAgreement)}
          <div className={style.bottomMarker}>— 已到达全部规则最底部 —</div>
        </div>
      </Modal>
    );
  }
}

export default LegalConfirmationModal;
